/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createOperationCall, createOperationCallAndPoll, OperationHandler } from 'src/core/services/api/createOperationCall';
import { fileService } from 'src/core/services/api/fileService';
import { httpService } from 'src/core/services/api/httpService';
import { OperationStatusBaseSwagger } from 'src/core/services/api/operationSwagger';
import { FileType } from 'src/core/types/FileType';
import { ResponseResourcesValue } from 'src/core/types/genericApiResponse';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { JobCreateSwagger } from 'src/customText/core/services/jobsSwagger';
import {
    LogicalOperation,
    QnaMakerPredictedQnASwagger,
    QnaMakerProjectCreationPayloadSwagger,
    QnaMakerProjectSwagger
} from 'src/qnaMaker/core/services/qnaMakerProjectSwaggers';
import { QnaMakerMetadataType } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { qnaMakerGenericFailureError } from 'src/qnaMaker/core/utils/qnaMakerOperation';

/**
 * Represents a service for manipulating QnA service apps.
 * Note: This service only works in prod environments.
 */
export const getQnaMakerProjectService = createService(({ context, getEnvironment }) => {
    const { apiUrl } = getEnvironment({ apiName: 'qna' });

    const getDefaultApiOptions = () =>
        luisRequestOptionsBuilder()
            .useDefaultOptions(context.serviceKey, true)
            .build();

    const getDefaultUrl = (id?: string) =>
        urlBuilder()
            .baseUrl(apiUrl)
            .project(id)
            .queryParam('api-version', LuisConstants.QnaMaker.ApiVersion);

    /**
     * Gets a list of QnA projects.
     */
    const getProjects = async (): Promise<QnaMakerProjectSwagger[]> => {
        const url = getDefaultUrl().build();

        const { data } = await httpService.get<ResponseResourcesValue<QnaMakerProjectSwagger>>(url, getDefaultApiOptions());

        return data.value;
    };

    /**
     * Gets a QnA project by it's name.
     */
    const getProject = async ({ projectId }: { projectId: string }): Promise<QnaMakerProjectSwagger> => {
        const url = getDefaultUrl(projectId).build();

        const { data } = await httpService.get<QnaMakerProjectSwagger>(url, getDefaultApiOptions());

        return data;
    };

    /**
     * Creates or updates a qna maker project.
     * @param param0.payload the new project data.
     */
    const createOrUpdateProject = async ({
        payload
    }: {
        payload: QnaMakerProjectCreationPayloadSwagger;
    }): Promise<QnaMakerProjectSwagger> => {
        const { data: projectSwagger } = await httpService.patch<QnaMakerProjectSwagger>(
            getDefaultUrl(payload.projectName).build(),
            payload,
            getDefaultApiOptions()
        );

        return projectSwagger;
    };

    /**
     * Deletes a qna maker project.
     */
    const deleteProject = async ({ projectName }: { projectName: string }) =>
        await httpService.delete(getDefaultUrl(projectName).build(), getDefaultApiOptions());

    const exportProject = async ({
        projectName,
        fileType
    }: {
        projectName: string;
        fileType: Extract<FileType, 'excel' | 'tsv'>;
    }): Promise<void> => {
        const url = getDefaultUrl(projectName)
            .resource(':export')
            .queryParam('format', fileType)
            .build();

        // To be refactored when the backend returns the whole path in the location header
        const getJobStatusUrl = (_jobId: string) =>
            getDefaultUrl(projectName)
                .resource('export')
                .resource('jobs', _jobId)
                .build();

        const pathOp = httpService.post<void>(url, {}, getDefaultApiOptions());

        let jobId;
        await createOperationCallAndPoll<{ resultUrl: string } & OperationStatusBaseSwagger>(
            pathOp,
            getDefaultApiOptions(),
            {
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                handleCall: res => {
                    jobId = res.jobId;
                },
                retryStrategy: res => res.data.status.toLowerCase() !== 'succeeded' && res.data.status.toLowerCase() !== 'failed'
            },
            // ToDo: will be revisited once the backend returns the whole URI in the location header
            response =>
                getJobStatusUrl(
                    response.headers[LuisConstants.OperationHeaders.LocationHeader]
                        .split('/')
                        .pop()
                        .split('?')[0]
                ),
            LuisConstants.QnaMaker.LroPollMaxNumberOfTrials
        );

        const resultUrl = getDefaultUrl(projectName)
            .resource('export')
            .resource('jobs', jobId)
            .resource('result')
            .build();
        await fileService.download(resultUrl, `${projectName}.zip`, getDefaultApiOptions());
    };

    const importProject = async ({
        projectName,
        fileType,
        file
    }: {
        projectName: string;
        fileType: Extract<FileType, 'excel' | 'tsv'>;
        file: File;
    }): Promise<void> => {
        const url = getDefaultUrl(projectName)
            .resource(':import')
            .queryParam('format', fileType)
            .build();

        // To be refactored when the backend returns the whole path in the location header
        const getJobStatusUrl = (jobId: string) =>
            getDefaultUrl(projectName)
                .resource('import')
                .resource('jobs', jobId)
                .build();

        const formData = new FormData();
        formData.append('files', file, file.name);
        const multipartApiOptions = luisRequestOptionsBuilder()
            .useProperServiceKey(context.serviceKey, true)
            .build();
        const pathOp = httpService.post<void>(url, formData, multipartApiOptions);

        await createOperationCallAndPoll<{ resultUrl: string } & OperationStatusBaseSwagger>(
            pathOp,
            getDefaultApiOptions(),
            {
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                retryStrategy: res => res.data.status.toLowerCase() !== 'succeeded' && res.data.status.toLowerCase() !== 'failed',
                handleCall: res => {
                    if (res.status === 'failed') {
                        let err;
                        if (res.errors?.length) {
                            err = new Error(
                                res.errors[0].message.concat(`\n ${res.errors[0].details?.map(d => `- ${d.code}: ${d.message}\n`)}`)
                            );
                            err.name = res.errors[0].code;
                        } else {
                            err = qnaMakerGenericFailureError(res.jobId);
                        }

                        throw err;
                    }
                }
            },
            // ToDo: will be revisited once the backend returns the whole URI in the location header
            response =>
                getJobStatusUrl(
                    response.headers[LuisConstants.OperationHeaders.LocationHeader]
                        .split('/')
                        .pop()
                        .split('?')[0]
                ),
            LuisConstants.QnaMaker.LroPollMaxNumberOfTrials
        );
    };

    /**
     * Deploys a QnA project.
     */
    const deployProject = async ({
        projectName,
        operationHandle
    }: {
        projectName: string;
        operationHandle: OperationHandler<JobCreateSwagger<object>>;
    }): Promise<void> => {
        const deployUrlBuilder = getDefaultUrl(projectName).resource('deployments', 'production');

        const { headers } = await httpService.put(deployUrlBuilder.build(), {}, getDefaultApiOptions());

        const jobUrl = (<Record<string, string>>headers)[LuisConstants.OperationHeaders.LocationHeader];
        // tslint:disable-next-line: no-suspicious-comment
        // ToDo: will be revisited once the backend returns the whole URI in the location header
        const jobId = jobUrl
            .split('/')
            .pop()
            .split('?')[0];

        await createOperationCall(
            getDefaultUrl(projectName)
                .resource('deployments', 'production')
                .resource('jobs', jobId)
                .build(),
            getDefaultApiOptions(),
            operationHandle
        );
    };

    const predict = async ({
        question,
        metadata,
        projectName,
        useDeployedQnA,
        logicalOperation,
        includeShortAnswer,
        confidenceScoreThreshold,
        previousQnAId,
        previousUserQuery,
        qnaId
    }: {
        question: string;
        projectName: string;
        useDeployedQnA: boolean;
        includeShortAnswer: boolean;
        metadata: QnaMakerMetadataType;
        confidenceScoreThreshold: number;
        logicalOperation: LogicalOperation;
        previousQnAId: number;
        previousUserQuery: string;
        qnaId?: number;
    }): Promise<QnaMakerPredictedQnASwagger> => {
        const { apiUrl: runtimeApiUrl } = getEnvironment({ apiName: 'qnaRuntime' });
        let predictUrlBuilder = urlBuilder()
            .baseUrl(runtimeApiUrl)
            .resource(':query-knowledgebases')
            .queryParam('projectName', projectName)
            .queryParam('api-version', LuisConstants.QnaMaker.ApiVersion);

        if (!useDeployedQnA) {
            predictUrlBuilder = predictUrlBuilder.queryParam('deploymentName', 'test');
        } else {
            predictUrlBuilder = predictUrlBuilder.queryParam('deploymentName', 'production');
        }

        const predictUrl = predictUrlBuilder.build();

        const { data: prediction } = await httpService.post<QnaMakerPredictedQnASwagger>(
            predictUrl,
            {
                qnaId,
                question,
                top: 3,
                confidenceScoreThreshold,
                filters: {
                    metadataFilter: { metadata: Object.entries(metadata).map(([key, value]) => ({ key, value })), logicalOperation }
                },
                answerSpanRequest: {
                    enable: includeShortAnswer,
                    confidenceScoreThreshold,
                    topAnswersWithSpan: 1
                },
                context: {
                    previousQnAId,
                    previousUserQuery
                },
                includeUnstructuredSources: true
            },
            getDefaultApiOptions()
        );

        return prediction;
    };

    return {
        predict,
        getProject,
        getProjects,
        deleteProject,
        exportProject,
        importProject,
        deployProject,
        createOrUpdateProject
    };
});
